/**
 * The host the partner logic reads: `<partner>[-env].<ea|ulc>…` (first label = partner,
 * second = ea|ulc). Everywhere real this is the browser host, unchanged.
 *
 * Dev workspaces whose URL carries no partner (Coder: learner--<ws>--<owner>.<domain>) set
 * VITE_PARTNER_HOST to a virtual host such as `welcome-dev.ulc`. `?partner_host=accelerate-dev.ea`
 * switches it: read once, kept in localStorage (in memory when storage is blocked), then
 * removed from the URL so a later switch sticks. Unset VITE_PARTNER_HOST, or any production
 * build, = off.
 */
const STORAGE_KEY = 'partnerHostOverride';
const QUERY_PARAM = 'partner_host';
const VIRTUAL_HOST = /^[a-z0-9-]+\.(ea|ulc)$/;

// This page's latest switch; wins over storage, and is all there is when storage is blocked.
let pageHost = null;

// Dev server only: a production build compiles DEV to false and drops the override.
export function isPartnerHostOverridden() {
  return import.meta.env.DEV === true && Boolean(import.meta.env.VITE_PARTNER_HOST);
}

/**
 * Applies `?partner_host=` and strips it from the URL. Runs before the router reads the URL
 * (router.js), and again from getPartnerHost() so it never depends on that order.
 */
export function consumePartnerHostParam(win = window) {
  if (!isPartnerHostOverridden()) return;
  const params = new URLSearchParams(win.location.search);
  if (!params.has(QUERY_PARAM)) return;

  setPartnerHost(params.get(QUERY_PARAM));
  params.delete(QUERY_PARAM);
  if (win.history) {
    const search = params.toString();
    const { pathname, hash } = win.location;
    win.history.replaceState(win.history.state, '', `${pathname}${search ? `?${search}` : ''}${hash}`);
  }
}

export function getPartnerHost(win = window) {
  if (!isPartnerHostOverridden()) return win.location.host;
  consumePartnerHostParam(win);
  if (pageHost) return pageHost;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (VIRTUAL_HOST.test(stored || '')) return stored;
  } catch (e) {
    // storage blocked: fall back to the configured default
  }
  return import.meta.env.VITE_PARTNER_HOST;
}

/** Switches the virtual host; ignores (returns false) anything but `<partner>[-env].<ea|ulc>`. */
export function setPartnerHost(host) {
  const normalized = String(host || '').toLowerCase();
  if (!VIRTUAL_HOST.test(normalized)) return false;
  pageHost = normalized;
  try {
    localStorage.setItem(STORAGE_KEY, normalized);
  } catch (e) {
    // storage blocked: pageHost keeps it for this page
  }
  return true;
}

/**
 * Same-origin URL that reloads the app under `host` (the param is consumed on load). Keeps path,
 * query and hash, except on the login callback: its one-time tokens can't be replayed, so a
 * login lands on the dashboard.
 */
export function partnerHostSwitchUrl({ origin, pathname, search, hash }, host) {
  const url = /\/callback$/.test(pathname)
    ? new URL(pathname.replace(/callback$/, 'dashboard'), origin)
    : new URL(`${pathname}${search}${hash}`, origin);
  url.searchParams.set(QUERY_PARAM, host);
  return url.toString();
}
